import dayjs from "dayjs";
import {
  TrackingSensorDto,
  TrackingSensorMileageHistoryDto,
  TrackingSensorMileagePointDto,
  TrackingSensorPathDto,
  TrackingSensorPointDto,
  TrackingSensorEventDto,
} from "../../dto/Deal/DealDto";

export type TrackingSensorPointModel = Omit<TrackingSensorPointDto, "date" | "squarePoints"> & {
  date: dayjs.Dayjs;
  squarePoints?: Array<TrackingSensorPointModel>;
  sourceIndex: number;
};

export type TrackingSensorPathModel = TrackingSensorPathDto;

export type TrackingSensorMileagePointModel = Omit<TrackingSensorMileagePointDto, "date"> & {
  date: dayjs.Dayjs;
};

export type TrackingSensorMileageHistoryModel = Omit<TrackingSensorMileageHistoryDto, "from" | "to"> & {
  from: TrackingSensorMileagePointModel | null;
  to: TrackingSensorMileagePointModel;
};

export type TrackingSensorEventModel = Omit<TrackingSensorEventDto, "date"> & { date: dayjs.Dayjs | null };

type TrackingSensorModel = Omit<TrackingSensorDto, "points" | "paths" | "mileageHistory" | "lastSyncAt" | "nextSyncAt" | "events"> & {
  events?: Array<TrackingSensorEventModel> | null;
  lastSyncAt: dayjs.Dayjs | null;
  nextSyncAt: dayjs.Dayjs | null;
  points: Array<TrackingSensorPointModel>;
  paths: Array<TrackingSensorPathModel>;
  mileageHistory?: Array<TrackingSensorMileageHistoryModel> | null;
};

export class TrackingSensorDataConverter {
  static convertTrackingSensorPoint(point: TrackingSensorPointDto, sourceIndex: number): TrackingSensorPointModel {
    return {
      ...point,
      date: dayjs(point.date ?? null),
      squarePoints: point.squarePoints?.map((raw, index) => this.convertTrackingSensorPoint(raw, index)),
      metadata: point.metadata ?? {},
      sourceIndex,
    };
  }

  static convertTrackingSensorPoints(points: Array<TrackingSensorPointDto> = []): Array<TrackingSensorPointModel> {
    return points
      .map((point, sourceIndex) => this.convertTrackingSensorPoint(point, sourceIndex))
      .sort((first, second) => first.date.valueOf() - second.date.valueOf());
  }

  static convertTrackingSensorPaths(paths: Array<TrackingSensorPathDto> = []): Array<TrackingSensorPathModel> {
    return paths;
  }

  static convertTrackingSensorMileageHistory(
    mileageHistory?: Array<TrackingSensorMileageHistoryDto> | null,
  ): Array<TrackingSensorMileageHistoryModel> | null | undefined {
    if (mileageHistory === undefined || mileageHistory === null) {
      return mileageHistory;
    }

    return mileageHistory.map((entry) => ({
      ...entry,
      from: entry.from ? { ...entry.from, date: dayjs(entry.from.date) } : null,
      to: { ...entry.to, date: dayjs(entry.to.date) },
    }));
  }

  static convertTrackingSensor(sensor: TrackingSensorDto): TrackingSensorModel {
    return {
      ...sensor,
      metadata: sensor.metadata ?? {},
      events:
        sensor.events == null
          ? sensor.events
          : sensor.events.map((event) => ({ ...event, date: event.date ? dayjs(event.date) : null, metadata: event.metadata ?? {} })),
      lastSyncAt: sensor.lastSyncAt ? dayjs(sensor.lastSyncAt) : null,
      nextSyncAt: sensor.nextSyncAt ? dayjs(sensor.nextSyncAt) : null,
      points: this.convertTrackingSensorPoints(sensor.points),
      paths: this.convertTrackingSensorPaths(sensor.paths),
      mileageHistory: this.convertTrackingSensorMileageHistory(sensor.mileageHistory),
    };
  }

  static convertTrackingSensors(sensors: Array<TrackingSensorDto> = []): Array<TrackingSensorModel> {
    return sensors.map((sensor) => this.convertTrackingSensor(sensor));
  }
}

export default TrackingSensorModel;
